import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import {
  Institute,
  InstitutesResponse,
  InstitutePayload,
  InstituteSubjectPayload,
  CategoriesInstitutes,
} from '../interfaces/institutes.interfaces';
import { TypeInstitute } from '../interfaces/typeInstite.interface';
import { City } from '../interfaces/city.interfaces';
import { EconomicGroup } from '../../economic-groups/interfaces/economicGroups.interfaces';
import { Catalog } from '../../../shared/interfaces/catalogs.interfaces';
import { AccessState } from '../../../../types';

export const institutesTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Institutes', 'InstitutesToCombo', 'InstituteSubjectsToCombo', 'InstituteSubjects'],
});
// Define a service using a base URL and expected endpoints
export const institutesApi = institutesTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all institutes
     * @Permission institutes-listar-institutos
     */
    getInstitutes: builder.query<
      InstitutesResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        sort?: string;
        type_sort?: string;
        economicGroupId?: number;
        typeInstituteId?: number;
        statusId?: number;
        valueMin?: string;
        valueMax?: string;
      }
    >({
      queryFn: async ({
        profileId,
        page,
        search,
        perPage,
        sort,
        type_sort,
        economicGroupId,
        typeInstituteId,
        valueMin,
        valueMax,
        statusId,
      }) => {
        try {
          const { data } = await mainApi.get<InstitutesResponse>('/institutes', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: type_sort || 'desc',
              ...(sort && { sort }),
              ...(economicGroupId && { economic_group_id: economicGroupId }),
              ...(typeInstituteId && { type_institute_id: typeInstituteId }),
              ...(statusId && { status_id: statusId }),
              ...(valueMin && { eco_gro_min_amount: valueMin }),
              ...(valueMax && { eco_gro_max_amount: valueMax }),
              filter_all: 1,
              search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        !Array.isArray(result) && result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Institutes', id } as const)),
              { type: 'Institutes', id: 'LIST' },
            ]
          : [{ type: 'Institutes', id: 'LIST' }],
    }),

    /**
     * @GET institutes to combo
     * @Permission institutes-listar-institutos
     */
    getAvailableInstitutes: builder.query<
      Institute[],
      { profileId?: number; typeInstituteId?: number }
    >({
      queryFn: async ({ profileId, typeInstituteId }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Institute[]>('/institutes', {
            params: {
              user_profile_id,
              ...(!!typeInstituteId && { type_institute_id: typeInstituteId }),

              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'InstitutesToCombo', id: 'LIST' }],
    }),

    getCategoriesOfInstitutes: builder.query<CategoriesInstitutes[], void>({
      queryFn: async () => {
        try {
          const { data } = await mainApi.get<CategoriesInstitutes[]>(`/category-institutes`, {
            params: {
              data: 'all',
            },
          });
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET institute by id
     * @Permission institutes-obtener-instituto
     */
    getInstitute: builder.query<Institute, { profileId: number; instituteId: number }>({
      queryFn: async ({ profileId, instituteId }) => {
        try {
          const { data } = await mainApi.get<Institute>(`/institutes/${instituteId}`, {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { instituteId, profileId }) => [
        { type: 'Institutes', profileId },
        { type: 'InstituteSubjectsToCombo', instituteId },
      ],
    }),

    /**
     * @POST create institute
     * @Permission institutes-crear-instituto
     */
    addInstitute: builder.mutation<
      Institute,
      { profileId: number; institutePayload: Partial<InstitutePayload> }
    >({
      queryFn: async ({ profileId, institutePayload }) => {
        try {
          const { data } = await mainApi.post<Institute>('/institutes', {
            user_profile_id: profileId,
            ...institutePayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Institutes', id: 'LIST' },
        { type: 'InstitutesToCombo', id: 'LIST' },
      ],
    }),

    /**
     * @PUT update institute
     * @Permission institutes-actualizar-instituto
     */
    updateInstitute: builder.mutation<
      Institute,
      { profileId: number; instituteId: number; institutePayload: Partial<InstitutePayload> }
    >({
      queryFn: async ({ profileId, instituteId, institutePayload }) => {
        try {
          const { data } = await mainApi.put<Institute>(`/institutes/${instituteId}`, {
            user_profile_id: profileId,
            ...institutePayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Institutes', id: 'LIST' },
        { type: 'InstitutesToCombo', id: 'LIST' },
      ],
    }),

    /**
     * @DELETE remove institute
     * @Permission institutes-eliminar-instituto
     */
    deleteInstitute: builder.mutation<Institute, { profileId: number; instituteId: number }>({
      queryFn: async ({ profileId, instituteId }) => {
        try {
          const { data } = await mainApi.delete(`institutes/${instituteId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Institutes', id: 'LIST' },
        { type: 'InstitutesToCombo', id: 'LIST' },
      ],
    }),

    /**
     * @GET type institute availables to combo box
     * @Permission institutetype-listar-tipos-de-institutos
     */
    getAvailablesTypeInstitute: builder.query<TypeInstitute[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get('/institutetypes', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET type institute availables to combo box
     */
    getAvailablesCities: builder.query<City[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get('/catalogs/c/children', {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data: Array.isArray(data.children) ? data.children : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET provinces availables to combo box
     */
    getAvailablesProvinces: builder.query<Catalog[], { profileId?: number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get('/catalogs/pais-ec/children-keywork', {
            params: {
              user_profile_id,
            },
          });
          const dataSort = Array.isArray(data.children)
            ? data.children.sort((a: Catalog, b: Catalog) => (a.cat_name > b.cat_name ? 1 : -1))
            : [];
          return { data: dataSort };
        } catch (error: any) {
          return { error };
        }
      },
    }),
    /**
     * @GET economic group availables to combo box
     * @Permission economic_group-listar-grupo-economico
     */
    getAvailablesEconomicGroup: builder.query<EconomicGroup[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<EconomicGroup[]>('/economic-group', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST create institute subject
     */
    addInstituteSubject: builder.mutation<
      Institute,
      { profileId: number; instituteSubjectPayload: Partial<InstituteSubjectPayload> }
    >({
      queryFn: async ({ profileId, instituteSubjectPayload }) => {
        try {
          const { data } = await mainApi.post<Institute>('/institution-subjects', {
            user_profile_id: profileId,
            ...instituteSubjectPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (_res, _err, { instituteSubjectPayload: { institute_id } }) => [
        // { type: 'InstituteSubjects', id: 'LIST' },
        { type: 'InstituteSubjectsToCombo', institute_id },
      ],
    }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetInstitutesQuery,
  useLazyGetInstitutesQuery,
  useAddInstituteMutation,
  useDeleteInstituteMutation,
  useGetInstituteQuery,
  useUpdateInstituteMutation,
  useGetAvailablesTypeInstituteQuery,
  useGetAvailablesCitiesQuery,
  useGetAvailablesProvincesQuery,
  useGetAvailablesEconomicGroupQuery,
  useGetAvailableInstitutesQuery,
  useAddInstituteSubjectMutation,
  useGetCategoriesOfInstitutesQuery,
} = institutesApi;
